<template>
  <div class="tab-content-container">
    <div class="tab-header-container">
      <h2 class="form-label">Информация за екипа</h2>
      <LiteButton @click.native="editTeamInfoButtonClicked"
                  class="edit-button"
                  v-show="!shouldEditTeamInfo && isUserAdmin"
                  :hoverColor="'blue'"
                  :icon="'far fa-edit'">
      </LiteButton>
      <LiteButton @click.native="editTeamInfoButtonClicked"
                  style="font-size: 27px;"
                  class="edit-button"
                  v-show="shouldEditTeamInfo && isUserAdmin"
                  :hoverColor="'red'"
                  :icon="'far fa-window-close'">
      </LiteButton>

      <div class="last-modified-container">
        <p class="last-modified-date" v-text="getTeamOverviewLastModifiedDate"></p>
        <p class="last-modified-name" v-text="getTeamOverviewLastModifiedBy"></p>
      </div>
    </div>

    <hr class="form-line">

    <!--    when preview    -->

    <!--     team info       -->
    <div class="text-container" v-if="!shouldEditTeamInfo">
      <div class="form-column-full-width">
        <TextField class="text-field"
                   :label="'Име'"
                   :value="teamInfo.name">
        </TextField>

        <TextField class="text-field"
                   :label="'Локация'"
                   :value="teamInfo.location">
        </TextField>

        <TextField class="text-field"
                   :label="'ISO'"
                   :value="teamInfo.country ? teamInfo.country.iso : '-'">
        </TextField>
      </div>
    </div>

    <!--    when edit team info   -->
    <form class="form-container" v-if="shouldEditTeamInfo">
      <div class="form-column-full-width">
        <InputText class="input"
                   @value-change="setAttribute"
                   :id="'name'"
                   :name="'name'"
                   :label="'Име'"
                   :initialValue="teamInfo.name"
                   :placeholder="'Въведете име'"
                   :showError="shouldShowError('name')">
        </InputText>
        <ErrorInput class="error" v-if="shouldShowError('name')" :text="errors['name']"/>

        <InputText class="input"
                   @value-change="setAttribute"
                   :id="'location'"
                   :name="'location'"
                   :label="'Локация'"
                   :initialValue="teamInfo.location"
                   :placeholder="'Въведете локация'">
        </InputText>

        <SelectDropDown v-if="countries"
                        class="input"
                        :id="'countryDropdown'"
                        :name="'countryIso'"
                        :options="countries"
                        :placeholder="'Изберете ISO'"
                        :label="'Държава'"
                        :selector="'iso'"
                        :defaultSelected="{id: teamOverview.country ? teamOverview.country.iso : null}"
                        @selection-change="setCountry"
        />
      </div>

      <Button class="save-button"
              @click.native="saveTeamOverview"
              :color="'green'"
              :text="'Запази'"
              :icon="'fa fa-save'"></Button>
    </form>

    <hr class="form-line">

    <div class="tab-header-container">
      <h2 class="form-label">Професии</h2>
      <LiteButton @click.native="editProfessionsButtonClicked"
                  class="edit-button"
                  v-show="!shouldEditProfessions && isUserAdmin"
                  :hoverColor="'blue'"
                  :icon="'far fa-edit'">
      </LiteButton>
      <LiteButton @click.native="editProfessionsButtonClicked"
                  style="font-size: 27px;"
                  class="edit-button"
                  v-show="shouldEditProfessions && isUserAdmin"
                  :hoverColor="'red'"
                  :icon="'far fa-window-close'">
      </LiteButton>
    </div>

    <hr class="form-line">

    <!--     team professions       -->
    <draggable v-model="teamProfessionsCache" handle=".handle" group="profession" @start="drag=true"
               @end="drag=false">
      <div class="item-container very-light-shadow"
           v-for="profession in teamProfessionsCache" :key="profession.professionId">

                <span v-show="shouldEditProfessions">
                    <i class="fas fa-grip-vertical grip-icon handle"></i>
                </span>

        <p class="item-label" v-text="profession.professionName"></p>

        <ItemsCounter class="item-info"
                      v-if="!shouldEditProfessions"
                      :label="'Брой работници'"
                      :count="profession.currentWorkerCount"
                      :maxCount="profession.maxWorkerCount"/>

        <InputText class="input-max-worker-count"
                   v-if="shouldEditProfessions"
                   @value-change="maxWorkerCountChanged"
                   :id="'max-worker-count-edit_' + profession.professionId"
                   :name="'max-worker-count-edit'"
                   :isNumber="true"
                   :initialValueNumber="profession.maxWorkerCount">
        </InputText>

        <LiteButton @click.native="removeProfession(profession)"
                    class="edit-button delete-icon"
                    v-show="shouldEditProfessions && profession.currentWorkerCount === 0"
                    :hoverColor="'red'"
                    :icon="'far fa-trash-alt'">
        </LiteButton>
        <div class="last-modified-container">
          <p class="last-modified-date" v-text="getLastModifiedDate(profession)"></p>
          <p class="last-modified-name" v-text="getLastModifiedBy(profession)"></p>
        </div>
      </div>
    </draggable>
    <div class="add-item-container" v-if="shouldEditProfessions">
      <SelectDropDown class="item-dropdown"
                      :id="'profession'"
                      :name="'profession'"
                      @selection-change="professionSelected"
                      :options="getProfessionsForDropDown"
                      :label="'Добавяне на професия'"
                      :placeholder="'Изберете професия'">
      </SelectDropDown>

      <InputText class="item-number-input"
                 :id="'professionCount'"
                 :name="'professionCount'"
                 :label="'Максимален брой работници'"
                 :isNumber="true"
                 :showError="shouldShowError('maxCount')"
                 :initialValueNumber="1"
                 @value-change="maxCountSelected"/>

      <LiteButton @click.native="addProfession"
                  class="add-item-icon"
                  :hoverColor="'green'"
                  :icon="'far fa-plus-square'">
      </LiteButton>
    </div>

    <Button class="save-button"
            v-if="shouldEditProfessions"
            @click.native="saveProfessions"
            :color="'green'"
            :text="'Запази'"
            :icon="'fa fa-save'"></Button>
  </div>
</template>

<script>
import {mapGetters} from 'vuex';
import InputText from "@/shared/components/form/InputText";
import ErrorInput from "@/shared/components/form/ErrorInput";
import Button from "@/shared/components/button/Button";
import LiteButton from "@/shared/components/button/LiteButton";
import TextField from "@/shared/components/TextField";
import teamService from "@/services/team.service";
import SelectDropDown from "@/shared/components/form/SelectDropDown";
import mapperService from "@/services/mapper.service";
import draggable from 'vuedraggable';
import {cloneDeep} from 'lodash';
import ItemsCounter from "@/shared/components/ItemsCounter";
import sharedService from "@/services/shared.service";

export default {
  name: "TeamInfo",
  components: {ItemsCounter, TextField, LiteButton, InputText, ErrorInput, Button, SelectDropDown, draggable},
  created() {
    this.$store.dispatch('fetchAllProfessions');
    this.teamInfo = cloneDeep(this.teamOverview);
    this.teamProfessionsCache = cloneDeep(this.teamProfessions);

    sharedService.fetchCountries()
        .then(({data}) => {
          // this.teamInfo.country.id = this.teamInfo.country ? this.teamInfo.country.iso : null;
          data.forEach((country) => country.id = country.iso);
          this.countries = data;
        });
  },
  data() {
    return {
      teamId: this.$route.params.id,
      shouldEditTeamInfo: false,
      shouldEditProfessions: false,
      teamInfo: cloneDeep(this.teamOverview),
      teamProfessionsCache: cloneDeep(this.teamProfessions),
      selectedProfession: null,
      selectedMaxCount: 1,
      countries: null,
      errors: {}
    }
  },
  methods: {
    setAttribute(value) {
      this.teamInfo[value.name] = value.currentValue;
      this.errors[value.name] = null;
    },
    setCountry(value) {
      this.teamInfo.country = value.currentValue;
    },
    maxWorkerCountChanged(value) {
      const professionId = value.id.split('_')[1];
      const {currentValue} = value;
      this.teamProfessionsCache.map(teamProfession => {
        if (teamProfession.professionId + '' === professionId) {
          teamProfession.maxWorkerCount = currentValue;
        }
        return teamProfession;
      });
    },
    shouldShowError(fieldName) {
      return !!(this.errors && this.errors[fieldName]);
    },
    editTeamInfoButtonClicked() {
      this.shouldEditTeamInfo = !this.shouldEditTeamInfo;
      this.teamInfo = cloneDeep(this.teamOverview);
    },
    editProfessionsButtonClicked() {
      this.shouldEditProfessions = !this.shouldEditProfessions;
      this.teamProfessionsCache = cloneDeep(this.teamProfessions);
    },
    saveTeamOverview() {
      teamService.updateOverview(this.teamInfo)
          .then(({data}) => {
            this.$store.commit('setTeamOverview', data);
            this.shouldEditTeamInfo = false;
          })
          .catch((errors) => this.errors = errors.response.data.errors);
    },
    removeProfession(profession) {
      this.teamProfessionsCache = this.teamProfessionsCache.filter((temp) => temp.professionId !== profession.professionId);
    },
    getLastModifiedDate(object) {
      return mapperService.getLastModifiedDate(object);
    },
    getLastModifiedBy(object) {
      return mapperService.getLastModifiedBy(object);
    },
    addProfession() {
      if (this.selectedProfession) {
        if (this.selectedMaxCount <= 0) {
          this.errors = {maxCount: 'Трябва да бъде положително число!'};
        } else {
          this.teamProfessionsCache.push(
              {
                teamId: this.teamId,
                professionName: this.selectedProfession.currentValue.name,
                professionId: this.selectedProfession.currentValue.id,
                orderIndex: this.getNextOrderIndex(),
                maxWorkerCount: this.selectedMaxCount,
                currentWorkerCount: 0
              }
          );
        }
      }
    },
    professionSelected(profession) {
      this.selectedProfession = profession;
    },
    maxCountSelected(maxCount) {
      this.selectedMaxCount = maxCount.currentValue;
      this.errors['maxCount'] = null;
    },
    saveProfessions() {
      teamService.updateProfessions(this.teamId, this.teamProfessionsCache).then(({data}) => {
        this.$store.commit('setTeamProfessions', data.teamProfessions);
        this.$store.commit('setTeamMembers', data.members);
        this.shouldEditProfessions = false;
      });
    },
    getNextOrderIndex() {
      if (this.teamProfessionsCache.length === 0) {
        return 0;
      } else {
        return this.teamProfessionsCache[this.teamProfessionsCache.length - 1].orderIndex + 1;
      }
    }
  },
  computed: {
    ...mapGetters(['teamOverview', 'teamProfessions', 'allProfessions', 'isUserAdmin']),

    getTeamOverviewLastModifiedBy() {
      return mapperService.getLastModifiedBy(this.teamOverview);
    },
    getTeamOverviewLastModifiedDate() {
      return mapperService.getLastModifiedDate(this.teamOverview);
    },
    getProfessionsForDropDown() {
      const currentProfessionIds = this.teamProfessionsCache.map((profession) => profession.professionId);
      return this.allProfessions.filter((profession) => !currentProfessionIds.includes(profession.id));
    }
  },
  watch: {
    teamOverview: {
      handler(newVal) {
        if (newVal) {
          this.teamInfo = cloneDeep(newVal);
        }
      },
      deep: true
    },
    teamProfessions: {
      handler(newVal) {
        if (newVal) {
          this.teamProfessionsCache = cloneDeep(newVal);
        }
      },
      deep: true
    },
    teamProfessionsCache: {
      handler(newVal) {
        // change order index when new val
        let i = 0;
        newVal.forEach((item) => {
          item.orderIndex = i;
          i++;
        });
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.form-container {
  width: 100%;
  height: fit-content;
}

.form-column-full-width {
  width: 100%;
  padding: 20px 140px;
  margin: 0 auto;
  display: inline-grid;
  height: fit-content;
  box-sizing: border-box;
}

.input {
  height: 95px;
  width: 500px;
  margin: 20px auto 0 auto;
}

.input-max-worker-count {
  height: 56px;
  margin-top: -8px;
  width: 200px;
  position: absolute;
  right: 40%;
}

.add-item-container {
  margin-top: 50px;
  width: 100%;
  display: inline-block;
}

.item-dropdown {
  width: 400px;
  float: left;
}

.item-number-input {
  width: 300px;
  float: left;
  height: 95px;
  margin-left: 50px;
}

.add-item-icon {
  font-size: 40px;
  float: left;
  width: fit-content;
  height: 100%;
  margin: 36px;
  color: var(--slight-light-green);
}

.grip-icon {
  font-size: 25px;
  float: left;
  margin-left: -20px;
  margin-right: 20px;
  width: 30px;
  cursor: grab;
  color: var(--primary-blue);
}

.grip-icon:hover {
  color: var(--blue);
}

.grip-icon:active {
  cursor: grabbing;
  color: var(--dark-blue);
}

.item-info {
  position: absolute;
  right: 40%;
}
</style>
